import { useEffect, useRef } from 'react';
import { APP } from '../app/appConfig';

/**
 * Report a QR/link scan of a bhajan page.
 *
 * A scan is only countable by whoever the OS hands the URL to. A universal-link
 * QR (`bhajanmala.com/nodes/:id?a=…`) opens the native app for people who have
 * it — the app reports those. Everyone else lands on THIS page, so this page
 * reports theirs. The two cohorts are disjoint, so there is no double count.
 *
 * Both report through bhajanmala's own `/api/scan`, never straight to the link
 * tracker: the site (and the app) only ever talk to bhajanmala, and bhajanmala
 * talks to sgslinks — so the tracker can change without touching either client.
 *
 * The surface is passed through verbatim; `/api/scan` is the authority on which
 * surfaces are real, so this stays out of that list (and a new surface needs no
 * web change).
 */
export function scanBeaconUrlFor(
  nodeIdStr: string | undefined,
  search: string,
  enabled: boolean,
): string | null {
  // Only the tenant that has the projection surfaces emits `?a=` scans; the
  // caller passes the flag so this stays free of tenant-id branching.
  if (!enabled) return null;
  const nodeId = Number(nodeIdStr);
  if (!Number.isInteger(nodeId) || nodeId <= 0) return null;
  const surface = new URLSearchParams(search).get('a')?.trim();
  if (!surface) return null;
  return `/api/scan?id=${String(nodeId)}&a=${encodeURIComponent(surface)}`;
}

/** Fires the scan beacon at most once per mount, when the URL carries `?a=`. */
export function useScanBeacon(nodeIdStr: string | undefined, search: string): void {
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    // projectModePromo is the projection feature — the only source of `?a=` QRs.
    const url = scanBeaconUrlFor(nodeIdStr, search, APP.features.projectModePromo);
    if (!url) return;
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
    fired.current = true;
    try {
      navigator.sendBeacon(url);
    } catch {
      /* fire-and-forget: a scanner has already reached the bhajan */
    }
  }, [nodeIdStr, search]);
}
